import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { COOKIE_NAMES } from '../config/cookies';

export type CookieCategory = 'essential' | 'analytics';

export interface CookiePreferences {
  /** Always true — required for the site to function, cannot be disabled. */
  essential: boolean;
  analytics: boolean;
}

export interface CookieConsentContextValue {
  preferences: CookiePreferences;
  hasConsented: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updatePreferences: (updates: Partial<CookiePreferences>) => void;
  resetPreferences: () => void;
  isAllowed: (category: CookieCategory) => boolean;
  /** Whether the detailed preferences dialog is open. */
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
}

const COOKIE_NAME = COOKIE_NAMES.CONSENT;
const COOKIE_EXPIRATION_DAYS = 365;

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
};

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `expires=${expires.toUTCString()}`,
    'path=/',
    'SameSite=Lax',
  ].join('; ');
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Hydrate from the consent cookie on mount.
  useEffect(() => {
    try {
      const raw = getCookie(COOKIE_NAME);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
        setPreferences({ essential: true, analytics: parsed.analytics ?? false });
        setHasConsented(true);
      }
    } catch (error) {
      console.error('Failed to read cookie consent:', error);
    }
  }, []);

  const save = useCallback((next: CookiePreferences) => {
    try {
      setCookie(COOKIE_NAME, JSON.stringify(next), COOKIE_EXPIRATION_DAYS);
      setPreferences(next);
      setHasConsented(true);
    } catch (error) {
      console.error('Failed to persist cookie consent:', error);
    }
  }, []);

  const acceptAll = useCallback(() => save({ essential: true, analytics: true }), [save]);
  const rejectAll = useCallback(() => save({ essential: true, analytics: false }), [save]);

  const updatePreferences = useCallback(
    (updates: Partial<CookiePreferences>) =>
      setPreferences((prev) => {
        const next = { ...prev, ...updates, essential: true as const };
        save(next);
        return next;
      }),
    [save],
  );

  const resetPreferences = useCallback(() => {
    deleteCookie(COOKIE_NAME);
    setPreferences(defaultPreferences);
    setHasConsented(false);
  }, []);

  const isAllowed = useCallback((category: CookieCategory) => preferences[category], [preferences]);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      preferences,
      hasConsented,
      acceptAll,
      rejectAll,
      updatePreferences,
      resetPreferences,
      isAllowed,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
    }),
    [
      preferences,
      hasConsented,
      acceptAll,
      rejectAll,
      updatePreferences,
      resetPreferences,
      isAllowed,
      preferencesOpen,
    ],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return ctx;
}
